import { closedPaywallState, serializePaywallUrl } from '@/libs/paywall-url-state';
import { usePaywall } from '@/hooks/use-paywall';
import { useAuth } from '@/contexts/auth-context';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { useBillingOffersQuery } from '@/hooks/queries/use-billing-offers-query';
import { usePaidCheckoutStatusQuery } from '@/hooks/queries/use-paid-checkout-status-query';
import { useShopifyIntegrationQuery } from '@/hooks/queries/use-integrations-query';
import { usePlanCommitment } from '@/pages/paywall/_components/use-plan-commitment';
import { usePlansQuery } from '@/hooks/queries/use-plans-query';
import { useShopCurrency } from '@/hooks/queries/use-shop-currency';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { PLAN_IDS } from '@/lib/payload';
import { formatPlanPrice } from '@/libs/utils/format-plan-price';
import { formatSuccessFeeRate } from '@/libs/utils/format-success-fee-rate';
import { isPaidCheckoutPending, readBillingContext } from '@/libs/billing-access';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import {
  billingPlanWithOffer,
  isActivePaidSubscription,
  readShopifyPlanCatalogue,
} from '../plan-choice/plan-choice-offer';
import { PLAN_FEE_GRID, PlanTier, type PlanResponseData } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const planIdOf = (tier: PlanTier): string | null =>
  tier === PlanTier.GROWTH
    ? PLAN_IDS.GROWTH
    : tier === PlanTier.ESSENTIAL
      ? PLAN_IDS.ESSENTIAL
      : null;

const findPlan = (plans: PlanResponseData[] | undefined, tier: PlanTier) =>
  plans?.find((plan) => plan.id === planIdOf(tier));

/**
 * What the upsell quotes and how it upgrades, read from the plan catalogue the
 * rest of the dashboard prices from (never from the mock-up): the target plan's
 * monthly price, both success-fee rates, and the upgrade itself.
 *
 * Both first subscriptions and replacements share the commitment hook and its
 * explicit terms acceptance. No request starts from uncertain cached reads.
 */
export function usePlanLimitOffer(
  currentTier: PlanTier,
  targetTier: PlanTier,
  onLeave: () => void
) {
  const { t } = useTranslation();
  const locale = t('common.locale');
  const navigate = useNavigate();
  const { openUpgradeModal } = usePaywall();
  const location = useLocation();
  const { user } = useAuth();
  const brandId = user?.currentBrandId ?? null;
  const billing = useBillingContextQuery({ enabled: !!brandId });
  const candidate = billing.data;
  const context =
    candidate &&
    readBillingContext(candidate) &&
    candidate.brandId === brandId &&
    candidate.organizationId === user?.currentBrand?.organizationId
      ? candidate
      : undefined;
  const isStripe = context?.channel === 'stripe';
  const offers = useBillingOffersQuery({ context, enabled: isStripe });
  const returnTo =
    getMcpReturnTo(serializePaywallUrl(location.pathname + location.search, closedPaywallState)) ??
    getMcpReturnTo(new URLSearchParams(location.search).get('returnTo'));
  const paidStatus = usePaidCheckoutStatusQuery({ context, ...(returnTo ? { returnTo } : {}) });
  const needsLegacyShopify =
    context?.channel === 'shopify' && !readShopifyPlanCatalogue(context, undefined).isCanonicalFree;
  const shopCurrency = useShopCurrency({ enabled: needsLegacyShopify });
  const shopifyCatalogue = readShopifyPlanCatalogue(context, shopCurrency);
  const canonicalShopifyFree = shopifyCatalogue.isCanonicalFree;
  const plansQuery = usePlansQuery({
    currency: isStripe ? (context?.billingCurrency ?? undefined) : shopifyCatalogue.currency,
    enabled: !!brandId,
  });
  const subscription = useSubscriptionQuery({
    enabled: !!brandId && needsLegacyShopify,
    scopeKey: brandId,
  });
  const integration = useShopifyIntegrationQuery({ enabled: needsLegacyShopify });

  const plans = plansQuery.data?.plans;
  const targetPlan = billingPlanWithOffer(
    findPlan(plans, targetTier),
    context,
    isStripe ? offers.data : undefined
  );
  const currentPlan = findPlan(plans, currentTier);
  const currentBasisPoints = currentPlan
    ? currentPlan.successFee.basisPoints
    : PLAN_FEE_GRID.free.successFeeBasisPoints;

  const upgradeLabel = targetPlan
    ? t('planChoice.upgrade', {
        plan: t(`pricing.plan.${targetTier === PlanTier.GROWTH ? 'growth' : 'essential'}.name`),
        price: formatPlanPrice(targetPlan.price.amount, targetPlan.price.currencyCode, locale),
      })
    : null;

  const isLoading =
    plansQuery.isPending ||
    (!isStripe && !canonicalShopifyFree && (subscription.isPending || integration.isPending)) ||
    plansQuery.isFetching ||
    (!isStripe && !canonicalShopifyFree && (subscription.isFetching || integration.isFetching)) ||
    billing.isPending ||
    billing.isFetching ||
    (isStripe &&
      !!context?.capabilities.canCommitPaidPlan &&
      (offers.isPending || offers.isFetching));
  const hasLoadError =
    plansQuery.isError ||
    !!billing.error ||
    !!paidStatus.error ||
    (isStripe
      ? !!offers.error
      : !canonicalShopifyFree && (subscription.isError || integration.isError)) ||
    shopifyCatalogue.unavailable ||
    (plansQuery.isSuccess && !context && !billing.isPending);
  const currentSubscription =
    isStripe || canonicalShopifyFree ? context?.subscription : subscription.data;
  const hasPendingSubscription = currentSubscription?.status === 'pending';
  const hasPendingChange = !isStripe && !!currentSubscription?.pendingChange;
  const paidAvailable =
    !!context?.capabilities.canCommitPaidPlan &&
    context.cardSetup.exemption !== 'impersonation' &&
    (context.channel === 'shopify' ||
      (isStripe &&
        (currentSubscription?.status === 'active' || context.checkoutAvailability.paid) &&
        context.taxQualificationRequired === false &&
        !!context.billingCurrency));
  // Completing invoice details is presentation, not permission to start a charge.
  const canPreparePaidTax =
    isStripe &&
    !!context?.capabilities.canCommitPaidPlan &&
    context.cardSetup.exemption !== 'impersonation' &&
    (context.effectiveTier === PlanTier.FREE || context.effectiveTier === PlanTier.FREE_BETA) &&
    currentSubscription === null &&
    context.checkoutAvailability.freeCardSetup &&
    context.taxQualificationRequired === true &&
    !!context.billingCurrency;
  const isReady =
    !!brandId &&
    !!context &&
    !!targetPlan &&
    plansQuery.isSuccess &&
    (isStripe
      ? offers.isSuccess
      : canonicalShopifyFree || (subscription.isSuccess && integration.isSuccess)) &&
    !isLoading &&
    !hasLoadError &&
    !hasPendingSubscription &&
    !hasPendingChange &&
    context?.shopifyFreeApproval?.requirement !== 'pending' &&
    (paidAvailable || canPreparePaidTax);
  const commitment = usePlanCommitment({
    isChangeMode: isActivePaidSubscription(currentSubscription),
    hasShopifyIntegration: !!integration.data || canonicalShopifyFree,
    isCheckingIntegration: !isReady,
    onMissingIntegration: () => {
      onLeave();
      navigate('/onboarding/shopify');
    },
    billingContext: context,
    ...(returnTo ? { returnTo } : {}),
  });
  const isUpgrading = !!targetPlan && commitment.isCommitting(targetPlan.id);
  const paidOperation = context?.paidCheckout?.status === 'expired' ? null : context?.paidCheckout;
  const paidPending =
    isPaidCheckoutPending(context, commitment.chargeResult) ||
    (commitment.chargeResult?.kind === 'pending' && commitment.chargeResult.planId === 'free');
  const upgrade = () => openUpgradeModal('generic', targetTier);

  return {
    isLoading,
    hasLoadError,
    retry: () => {
      void Promise.allSettled([
        plansQuery.refetch(),
        subscription.refetch(),
        integration.refetch(),
        billing.refetch(),
        ...(isStripe ? [offers.refetch()] : []),
      ]);
    },
    isUpgradeDisabled: !isReady || isUpgrading || paidPending || commitment.isPlanChangePending,
    pendingLabel: paidPending
      ? t('planChoice.paid.pendingTitle')
      : hasPendingSubscription
        ? t('settings.subscription.status.pending')
        : hasPendingChange
          ? t('pricing.pendingChange.title')
          : null,
    planAwaitingTerms: commitment.planAwaitingTerms,
    dismissTerms: commitment.dismissTerms,
    resumeAfterAcceptance: commitment.resumeAfterAcceptance,
    upgradeLabel,
    currentRate: formatSuccessFeeRate(currentBasisPoints, locale),
    targetRate: targetPlan ? formatSuccessFeeRate(targetPlan.successFee.basisPoints, locale) : '',
    upgrade,
    isUpgrading,
    paidOperation,
    paidPending,
    canResumePaid: paidAvailable && isReady,
    checkPaid: () => void (paidStatus.canReadOperation ? paidStatus.refetch() : billing.refetch()),
    resumePaid: () => {
      if (paidOperation) commitment.commit(paidOperation.planId);
    },
    planChangeResult: commitment.planChangeResult,
    checkPlanChange: commitment.checkPlanChange,
    isCheckingPlanChange: commitment.isCheckingPlanChange,
  };
}
